import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { GetAllUsersWithAccessToTheModuleForTheUserFilterQuery } from 'src/stores/genericActivityList/queries';
import { IDO_ACTIVITY_HISTORY_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE } from 'src/stores/idoActivityHistoryList/localStorageKeys';
import {
	GetTheIdoActivityModuleBoxesForTheIdoQuery,
	GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery,
	GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery,
} from 'src/stores/idoActivityHistoryList/queries';
import createIdoActivityHistoryListState from 'src/stores/idoActivityHistoryList/state';

import { Activity, ActivityRunType } from 'src/models/activity';
import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterBoxesRunType, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import { GenericActivityListPagination, GenericActivityListSort } from 'src/models/genericActivityList';
import { IdoButton } from 'src/models/ido';
import { ModuleNameEnum } from 'src/models/module/enums';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import {
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';

import {
	GetAllUsersWithAccessToTheModuleForTheUserFilterIdoActivityHistoryListCallBack,
	GetTheIdoActivityModuleBoxesForTheIdoCallBack,
	GetTheIdoActivityModuleIdoActivitiesForTheIdoCallBack,
} from './callbacks';
import {
	AddQueryStoreSubscriptionPayload,
	AggregatePayload,
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	FetchIdoActivityHistoryListByIdAndInstancePayload,
	FetchIdoActivityHistoryListPayload,
	QueryStoreAggregatePayload,
	RollbackPaginationPagePayload,
	SetDateFilterPayload,
	SetDirectionFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetIdoRelationsFilterPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSearchTermPayload,
	SetSortPayload,
	SetViewPayload,
} from './interfaces';
import { DirectionFilter, IdoRelationFilterEntry, ViewFilter } from './types';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};
const DEFAULT_DATE_FILTER = 'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonAll';
const DEFAULT_DIRECTION_FILTER: DirectionFilter = 'combined';
const DEFAULT_VIEW_FILTER: ViewFilter = 'all';

const createDefaultPagination = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const buildKey = ({ aggregateId, uniqueListIdentifier }: AggregatePayload): string =>
	`${uniqueListIdentifier}#${aggregateId}`;

const splitKey = (
	key: string
): {
	aggregateId: string;
	uniqueListIdentifier: string;
} => {
	const separatorIndex = key.indexOf('#');
	if (separatorIndex < 0) {
		return {
			aggregateId: key,
			uniqueListIdentifier: '',
		};
	}

	return {
		aggregateId: key.substring(separatorIndex + 1),
		uniqueListIdentifier: key.substring(0, separatorIndex),
	};
};

export const useIdoActivityHistoryListStore = defineStore('idoActivityHistoryList', {
	actions: {
		addQueryStoreSubscription(payload: AddQueryStoreSubscriptionPayload): void {
			const key = buildKey(payload);
			this.queryStoreSubscriptions.set(key, payload.serializedQuery);
		},
		clearFetchStateForKey(key: string): void {
			this.fetchingKeys.delete(key);
			this.loadedKeys.delete(key);
		},
		clearIdoActivityHistoryList(payload: AggregatePayload): void {
			const key = buildKey(payload);
			this.activeQueryIdentifierByKey.delete(key);
			this.idoActivityHistoryList.delete(key);
		},
		clearSubscriptions(payload: QueryStoreAggregatePayload): void {
			const key = buildKey(payload);

			if (payload.queryStore !== undefined && this.queryStoreSubscriptions.has(key)) {
				const serializedQuery = this.queryStoreSubscriptions.get(key) as SerializedQuery;

				switch (serializedQuery.type) {
					case 'GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery':
						void payload.queryStore.unsubscribeQuery(
							GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery.fromJSON<GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery>(
								serializedQuery.query
							)
						);
						break;
					case 'GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery':
						void payload.queryStore.unsubscribeQuery(
							GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery.fromJSON<GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery>(
								serializedQuery.query
							)
						);
						break;
					default:
						break;
				}
			}

			this.queryStoreSubscriptions.delete(key);
		},
		deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
			this.idoActivityHistoryList.forEach((activityList, key) => {
				const activityListCopy = removeTheAggregateIdsFromTheCollection(
					activityList as never[],
					payload.aggregateIds
				) as unknown as Activity[];

				if (activityListCopy !== activityList) {
					const keyParts = splitKey(key);
					const previousTotalCount = this.idoActivityHistoryListTotalCount.get(key) ?? 0;

					if (activityListCopy.length === 0) {
						this.idoActivityHistoryListTotalCount.set(key, 0);
						this.idoActivityHistoryListSliced.set(key, new Map());
						this.idoActivityHistoryList.set(key, []);
						return;
					}

					const paginator = new Paginator(activityListCopy, DEFAULT_ROWS_PER_PAGE, 1);

					while (paginator.current <= paginator.total) {
						this.setIdoActivityHistoryList({
							aggregateId: keyParts.aggregateId,
							page: paginator.current,
							payload: paginator.page(paginator.current) as Activity[],
							totalCount: previousTotalCount,
							uniqueListIdentifier: keyParts.uniqueListIdentifier,
						});

						paginator.next();
					}
				}
			});
		},
		async fetchFilterBoxes(payload: QueryStoreAggregatePayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setFilterBoxesValue({
					aggregateId: payload.aggregateId,
					payload: [],
					uniqueListIdentifier: payload.uniqueListIdentifier,
				});
				return;
			}

			await payload.queryStore.query<Array<FilterBoxes>>({
				callback: new GetTheIdoActivityModuleBoxesForTheIdoCallBack(
					this,
					payload.uniqueListIdentifier,
					payload.aggregateId
				),
				createTime: new Date(),
				query: new GetTheIdoActivityModuleBoxesForTheIdoQuery(),
				responseType: 'json',
				runType: FilterBoxesRunType,
			});
		},
		async fetchFilterUsers(payload: QueryStoreAggregatePayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.setFilterUsersValue({
					aggregateId: payload.aggregateId,
					payload: [],
					uniqueListIdentifier: payload.uniqueListIdentifier,
				});
				return;
			}

			await payload.queryStore.query<Array<FilterUsers>>({
				callback: new GetAllUsersWithAccessToTheModuleForTheUserFilterIdoActivityHistoryListCallBack(
					this,
					payload.uniqueListIdentifier,
					payload.aggregateId
				),
				createTime: new Date(),
				query: new GetAllUsersWithAccessToTheModuleForTheUserFilterQuery(ModuleNameEnum.MODULE_IDO_ACTIVITY),
				responseType: 'json',
				runType: FilterUsersRunType,
			});
		},
		async fetchIdoActivityHistoryList(payload: FetchIdoActivityHistoryListPayload): Promise<void> {
			if (payload.queryStore === undefined) {
				this.clearIdoActivityHistoryList(payload);
				this.clearFetchStateForKey(buildKey(payload));
				return;
			}

			this.registerUnsubscribeFetchIdoActivityHistoryList(payload);
			const fetchToken = useGenericActivityListStore().beginListFetch(this.$id);
			this.markKeyFetching(payload);

			const key = buildKey(payload);
			const sort = this.sortForKey(key);
			const pagination = this.paginationForKey(key);
			const page = pagination.page;
			const query = new GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery(
				payload.idoButton,
				this.filteredBoxesForKey(key),
				this.filteredUsersForKey(key).map((filteredUser: FilterUsers) => filteredUser.userId),
				this.dateFilterForKey(key) as string,
				this.viewForKey(key),
				this.directionFilterForKey(key),
				payload.searchTerm,
				this.paginationForKey(key),
				sort
			);
			const queryIdentifier = query.uniqueQueryIdentifier();

			this.setPaginationPageAction({
				aggregateId: payload.aggregateId,
				page: page,
				uniqueListIdentifier: payload.uniqueListIdentifier,
			});
			this.setActiveQueryIdentifier({
				aggregateId: payload.aggregateId,
				queryIdentifier,
				uniqueListIdentifier: payload.uniqueListIdentifier,
			});

			const callback = new GetTheIdoActivityModuleIdoActivitiesForTheIdoCallBack(
				this,
				payload.uniqueListIdentifier,
				payload.aggregateId,
				page,
				queryIdentifier
			);
			callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
			await payload.queryStore.query<Activity[]>({
				callback,
				createTime: new Date(),
				query,
				responseType: 'json',
				runType: ActivityRunType,
			});
		},
		async fetchIdoActivityHistoryListByIdoAndInstanceId(
			payload: FetchIdoActivityHistoryListByIdAndInstancePayload
		): Promise<void> {
			if (payload.idoRelations !== undefined) {
				this.setIdoRelationsFilterAction({
					aggregateId: payload.aggregateId,
					idoRelations: payload.idoRelations,
					uniqueListIdentifier: payload.uniqueListIdentifier,
				});
			}

			if (payload.queryStore === undefined) {
				this.silentSetIdoActivityHistoryList(payload);
				this.markKeyLoaded(payload);
				return;
			}

			this.registerUnsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId(payload);
			const fetchToken = useGenericActivityListStore().beginListFetch(this.$id);
			this.markKeyFetching(payload);

			const key = buildKey(payload);
			const sort = this.sortForKey(key);
			const pagination = this.paginationForKey(key);
			const page = pagination.page;
			const query = new GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery(
				payload.idoId,
				payload.idoInstanceId,
				this.filteredBoxesForKey(key),
				this.filteredUsersForKey(key).map((filteredUser: FilterUsers) => filteredUser.userId),
				this.dateFilterForKey(key) as string,
				this.viewForKey(key),
				this.directionFilterForKey(key),
				payload.searchTerm,
				this.paginationForKey(key),
				sort,
				this.idoRelationsForKey(key)
			);
			const queryIdentifier = query.uniqueQueryIdentifier();

			this.setPaginationPageAction({
				aggregateId: payload.aggregateId,
				page: page,
				uniqueListIdentifier: payload.uniqueListIdentifier,
			});
			this.setActiveQueryIdentifier({
				aggregateId: payload.aggregateId,
				queryIdentifier,
				uniqueListIdentifier: payload.uniqueListIdentifier,
			});

			const callback = new GetTheIdoActivityModuleIdoActivitiesForTheIdoCallBack(
				this,
				payload.uniqueListIdentifier,
				payload.aggregateId,
				page,
				queryIdentifier
			);
			callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
			await payload.queryStore.query<Activity[]>({
				callback,
				createTime: new Date(),
				query,
				responseType: 'json',
				runType: ActivityRunType,
			});
		},
		async fetchTheCompleteListForExport(payload: CompleteListExportFetchPayload): Promise<Activity[] | null> {
			if (payload.queryStore === undefined) {
				return null;
			}

			const key = buildKey(payload);
			const pagination: GenericActivityListPagination = {
				page: 1,
				rowsPerPage: this.idoActivityHistoryListTotalCountForKey(key) + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
			};
			const filteredUserIds = this.filteredUsersForKey(key).map(
				(filteredUser: FilterUsers) => filteredUser.userId
			);
			const query = prepareQueryForCompleteListExport(
				payload.idoId !== undefined && payload.idoInstanceId !== undefined
					? new GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery(
							payload.idoId,
							payload.idoInstanceId,
							this.filteredBoxesForKey(key),
							filteredUserIds,
							this.dateFilterForKey(key) as string,
							this.viewForKey(key),
							this.directionFilterForKey(key),
							payload.searchTerm,
							pagination,
							this.sortForKey(key),
							this.idoRelationsForKey(key)
						)
					: new GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery(
							payload.idoButton as IdoButton,
							this.filteredBoxesForKey(key),
							filteredUserIds,
							this.dateFilterForKey(key) as string,
							this.viewForKey(key),
							this.directionFilterForKey(key),
							payload.searchTerm,
							pagination,
							this.sortForKey(key)
						)
			);

			let completeList: Activity[] | null = null;

			await payload.queryStore.query<Activity[]>({
				callback: new ResolveListForExportCallback<Activity>((list) => {
					completeList = list;
				}),
				createTime: new Date(),
				query,
				responseType: 'json',
				runType: ActivityRunType,
			});

			return completeList;
		},
		isLatestQueryIdentifier(payload: { queryIdentifier: string } & AggregatePayload): boolean {
			const key = buildKey(payload);
			return this.activeQueryIdentifierByKey.get(key) === payload.queryIdentifier;
		},
		markKeyFetching(payload: AggregatePayload): void {
			this.fetchingKeys.add(buildKey(payload));
		},
		markKeyLoaded(payload: AggregatePayload): void {
			this.fetchingKeys.delete(buildKey(payload));
			this.loadedKeys.add(buildKey(payload));
		},
		prepareForFreshFetchAction(payload: AggregatePayload): void {
			const key = buildKey(payload);
			const pagination = this.paginationForKey(key);

			pagination.page = 1;

			this.pagination.set(key, pagination);
			this.idoActivityHistoryListTotalCount.set(key, 0);
			this.idoActivityHistoryList.set(key, []);
			this.idoActivityHistoryListSliced.set(key, new Map());
		},
		registerUnsubscribeFetchIdoActivityHistoryList(payload: FetchIdoActivityHistoryListPayload): void {
			if (payload.queryStore === undefined) {
				return;
			}

			const key = buildKey(payload);
			const query = new GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery(
				payload.idoButton,
				this.filteredBoxesForKey(key),
				this.filteredUsersForKey(key).map((filteredUser: FilterUsers) => filteredUser.userId),
				this.dateFilterForKey(key) as string,
				this.viewForKey(key),
				this.directionFilterForKey(key),
				payload.searchTerm,
				this.paginationForKey(key),
				this.sortForKey(key)
			);

			this.addQueryStoreSubscription({
				aggregateId: payload.aggregateId,
				serializedQuery: {
					query: JSON.stringify(query),
					type: 'GetTheIdoActivityModuleIdoActivitiesForTheIdoQuery',
				},
				uniqueListIdentifier: payload.uniqueListIdentifier,
			});
		},
		registerUnsubscribeFetchIdoActivityHistoryListByIdoAndInstanceId(
			payload: FetchIdoActivityHistoryListByIdAndInstancePayload
		): void {
			if (payload.idoRelations !== undefined) {
				this.setIdoRelationsFilterAction({
					aggregateId: payload.aggregateId,
					idoRelations: payload.idoRelations,
					uniqueListIdentifier: payload.uniqueListIdentifier,
				});
			}

			if (payload.queryStore === undefined) {
				return;
			}

			const key = buildKey(payload);
			const query = new GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery(
				payload.idoId,
				payload.idoInstanceId,
				this.filteredBoxesForKey(key),
				this.filteredUsersForKey(key).map((filteredUser: FilterUsers) => filteredUser.userId),
				this.dateFilterForKey(key) as string,
				this.viewForKey(key),
				this.directionFilterForKey(key),
				payload.searchTerm,
				this.paginationForKey(key),
				this.sortForKey(key),
				this.idoRelationsForKey(key)
			);

			this.addQueryStoreSubscription({
				aggregateId: payload.aggregateId,
				serializedQuery: {
					query: JSON.stringify(query),
					type: 'GetTheIdoActivityModuleIdoActivitiesForTheIdoByIdoAndInstanceIdQuery',
				},
				uniqueListIdentifier: payload.uniqueListIdentifier,
			});
		},
		reset(payload: QueryStoreAggregatePayload): void {
			this.clearSubscriptions(payload);
			const key = buildKey(payload);

			this.activeQueryIdentifierByKey.delete(key);
			this.dateFilter.delete(key);
			this.directionFilter.delete(key);
			this.filterBoxes.delete(key);
			this.filteredBoxIds.delete(key);
			this.filterUsers.delete(key);
			this.filteredUsers.delete(key);
			this.idoActivityHistoryListTotalCount.delete(key);
			this.idoActivityHistoryList.delete(key);
			this.idoActivityHistoryListSliced.delete(key);
			this.idoRelationsFilter.delete(key);
			this.pagination.delete(key);
			this.sort.delete(key);
			this.searchTerm.delete(key);
			this.view.delete(key);
		},
		rollbackPaginationPageAction(payload: RollbackPaginationPagePayload): void {
			if (!this.isLatestQueryIdentifier(payload)) {
				return;
			}

			const key = buildKey(payload);
			const pagination = this.paginationForKey(key);

			pagination.page = Math.max(1, payload.page - 1);

			this.pagination.set(key, pagination);
		},
		setActiveQueryIdentifier(payload: { queryIdentifier: string } & AggregatePayload): void {
			const key = buildKey(payload);
			this.activeQueryIdentifierByKey.set(key, payload.queryIdentifier);
		},
		setDateFilterAction(payload: SetDateFilterPayload): void {
			const key = buildKey(payload);
			this.dateFilter.set(key, payload.dateFilter || '');
		},
		setDirectionFilterAction(payload: SetDirectionFilterPayload): void {
			const key = buildKey(payload);
			this.directionFilter.set(key, payload.directionFilter);
		},
		setFilterBoxesValue(payload: {
			aggregateId: string;
			payload: Array<FilterBoxes>;
			uniqueListIdentifier: string;
		}): void {
			const key = buildKey(payload);
			this.filterBoxes.set(key, payload.payload || []);
		},
		setFilterUsersValue(payload: {
			aggregateId: string;
			payload: Array<FilterUsers>;
			uniqueListIdentifier: string;
		}): void {
			const key = buildKey(payload);
			this.filterUsers.set(key, payload.payload || []);
		},
		setFilteredBoxesAction(payload: SetFilteredBoxesPayload): void {
			const key = buildKey(payload);
			this.filteredBoxIds.set(key, payload.boxIdFilter || []);
		},
		setFilteredUsersAction(payload: SetFilteredUsersPayload): void {
			const key = buildKey(payload);
			this.filteredUsers.set(key, payload.filterUsers || []);
		},
		setIdoActivityHistoryList(payload: SetListPayload): void {
			const key = buildKey(payload);
			let pagination = this.pagination.get(key);

			if (pagination === undefined) {
				pagination = createDefaultPagination();
				this.pagination.set(key, pagination);
			}

			this.idoActivityHistoryListTotalCount.set(key, payload.totalCount);

			let slicedCollection = this.idoActivityHistoryListSliced.get(key);
			if (slicedCollection === undefined) {
				slicedCollection = new Map<number, Array<Activity>>();
			}

			slicedCollection.set(payload.page, payload.payload);
			this.idoActivityHistoryListSliced.set(key, slicedCollection);

			const selectedSort = this.sortForKey(key);

			const list = [...slicedCollection.values()]
				.flatMap((entry) => entry)
				.sort(createCompareFunction<Activity>(selectedSort.property as keyof Activity, selectedSort.direction));

			this.idoActivityHistoryList.set(key, list);
		},
		setIdoRelationsFilterAction(payload: SetIdoRelationsFilterPayload): void {
			const key = buildKey(payload);
			this.idoRelationsFilter.set(key, payload.idoRelations);
		},
		setListLoadError(): void {
			const genericActivityListStore = useGenericActivityListStore();
			genericActivityListStore.setListLoadError();
		},
		setListViewModeLarge(value: boolean): void {
			localStorage.setItem(
				IDO_ACTIVITY_HISTORY_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
				JSON.stringify(value)
			);
			this.listViewModeLarge = value;
		},
		setPaginationAction(payload: SetPaginationPayload): void {
			const key = buildKey(payload);
			this.pagination.set(key, payload.pagination);
		},
		setPaginationPageAction(payload: SetPaginationPagePayload): void {
			const key = buildKey(payload);
			const pagination = this.paginationForKey(key);
			pagination.page = payload.page;
			this.pagination.set(key, pagination);
		},
		setSearchTermAction(payload: SetSearchTermPayload): void {
			const key = buildKey(payload);
			this.searchTerm.set(key, payload.searchTerm || '');
		},
		setSortAction(payload: SetSortPayload): void {
			const key = buildKey(payload);
			this.sort.set(key, payload.sort);
		},
		setTheIdoActivityHistoryListStateToReady(payload: SetListStatePayload): void {
			this.updateTheStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
		},
		setTheIdoActivityHistoryListStateToSeen(payload: SetListStatePayload): void {
			this.updateTheStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
		},
		setTheIdoActivityHistoryListStateToUnseen(payload: SetListStatePayload): void {
			this.updateTheStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
		},
		setViewAction(payload: SetViewPayload): void {
			const key = buildKey(payload);
			this.view.set(key, payload.view);
		},
		settleKeyFetching(payload: { queryIdentifier: string } & AggregatePayload): void {
			if (!this.isLatestQueryIdentifier(payload)) {
				return;
			}
			this.markKeyLoaded(payload);
		},
		silentSetIdoActivityHistoryList(payload: AggregatePayload): void {
			const key = buildKey(payload);
			this.activeQueryIdentifierByKey.delete(key);
			this.idoActivityHistoryListTotalCount.set(key, 0);
			this.idoActivityHistoryList.set(key, []);
			this.idoActivityHistoryListSliced.set(key, new Map());
			this.sort.delete(key);
			this.pagination.set(key, createDefaultPagination());
		},
		updateTheStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
			this.idoActivityHistoryList.forEach((activityList, key) => {
				const activityListCopy = setTheStateForTheAggregateIds(activityList, aggregateIds, status);

				if (activityListCopy.length > 0) {
					const keyParts = splitKey(key);
					const previousTotalCount = this.idoActivityHistoryListTotalCount.get(key) ?? 0;
					const paginator = new Paginator(activityListCopy, DEFAULT_ROWS_PER_PAGE, 1);

					while (paginator.current <= paginator.total) {
						this.setIdoActivityHistoryList({
							aggregateId: keyParts.aggregateId,
							page: paginator.current,
							payload: paginator.page(paginator.current) as Activity[],
							totalCount: previousTotalCount,
							uniqueListIdentifier: keyParts.uniqueListIdentifier,
						});

						paginator.next();
					}
				}
			});
		},
	},
	getters: {
		dateFilterForKey(state): (key: string) => DateRangeSelection | string | string[] {
			return (key: string): DateRangeSelection | string | string[] =>
				state.dateFilter.has(key)
					? (state.dateFilter.get(key) as DateRangeSelection | string | string[])
					: DEFAULT_DATE_FILTER;
		},
		directionFilterForKey(state): (key: string) => DirectionFilter {
			return (key: string): DirectionFilter =>
				state.directionFilter.has(key)
					? (state.directionFilter.get(key) as DirectionFilter)
					: DEFAULT_DIRECTION_FILTER;
		},
		filterBoxesForKey(state): (key: string) => Array<FilterBoxes> {
			return (key: string): Array<FilterBoxes> =>
				state.filterBoxes.has(key) ? (state.filterBoxes.get(key) as Array<FilterBoxes>) : [];
		},
		filterUsersForKey(state): (key: string) => Array<FilterUsers> {
			return (key: string): Array<FilterUsers> =>
				state.filterUsers.has(key) ? (state.filterUsers.get(key) as Array<FilterUsers>) : [];
		},
		filteredBoxesForKey(state): (key: string) => Array<string> {
			return (key: string): Array<string> =>
				state.filteredBoxIds.has(key) ? (state.filteredBoxIds.get(key) as Array<string>) : [];
		},
		filteredUsersForKey(state): (key: string) => Array<FilterUsers> {
			return (key: string): Array<FilterUsers> =>
				state.filteredUsers.has(key) ? (state.filteredUsers.get(key) as Array<FilterUsers>) : [];
		},
		idoActivityHistoryListForKey(state): (key: string) => Array<Activity> {
			return (key: string): Array<Activity> =>
				state.idoActivityHistoryList.has(key) ? (state.idoActivityHistoryList.get(key) as Array<Activity>) : [];
		},
		idoActivityHistoryListTotalCountForKey(state): (key: string) => number {
			return (key: string): number =>
				state.idoActivityHistoryListTotalCount.has(key)
					? (state.idoActivityHistoryListTotalCount.get(key) as number)
					: 0;
		},
		idoRelationsForKey(state): (key: string) => Array<IdoRelationFilterEntry> {
			return (key: string): Array<IdoRelationFilterEntry> =>
				state.idoRelationsFilter.has(key)
					? (state.idoRelationsFilter.get(key) as Array<IdoRelationFilterEntry>)
					: [];
		},
		isFetchingForKey(state): (key: string) => boolean {
			return (key: string): boolean => state.fetchingKeys.has(key) || !state.loadedKeys.has(key);
		},
		isFetchingPageForKey(state): (key: string) => boolean {
			return (key: string): boolean => state.fetchingKeys.has(key);
		},
		paginationForKey(state): (key: string) => GenericActivityListPagination {
			return (key: string): GenericActivityListPagination =>
				state.pagination.has(key) ? state.pagination.get(key) : createDefaultPagination();
		},
		searchTermForKey(state): (key: string) => string {
			return (key: string): string => (state.searchTerm.has(key) ? (state.searchTerm.get(key) as string) : '');
		},
		sortForKey(state): (key: string) => GenericActivityListSort {
			return (key: string): GenericActivityListSort =>
				state.sort.has(key) ? state.sort.get(key) : { ...DEFAULT_LIST_SORT };
		},
		viewForKey(state): (key: string) => ViewFilter {
			return (key: string): ViewFilter =>
				state.view.has(key) ? (state.view.get(key) as ViewFilter) : DEFAULT_VIEW_FILTER;
		},
	},
	state: createIdoActivityHistoryListState,
});
export * from './interfaces';
export * from './types';
